import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast, TradingToast } from '@trading-ui/core';

import { getCurrentUser } from '@/api/services/auth/auth.service';

import useAppStore from '@/store/app.store';

import { parseBackendReason } from '@/utils/error.utils';
import type { ProcessedBackendError } from '@/interfaces';

export const useBiofluxUser = (cognitoId: string | undefined) => {
  const user = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);
  const { addToast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['bioflux-user', cognitoId],
    queryFn: () => getCurrentUser(),
    enabled: !!cognitoId,
    retry: false,
  });

  useEffect(() => {
    if (data && !isLoading) {
      setUser(data);
    }
  }, [data, setUser, isLoading]);

  // Clear user from store when cognitoId becomes undefined (logout)
  useEffect(() => {
    if (!cognitoId && user) {
      setUser(null);
    }
  }, [cognitoId, user, setUser]);

  useEffect(() => {
    if (error) {
      const errorMessage = parseBackendReason(error as Error | ProcessedBackendError);
      addToast(<TradingToast type="error" title="Error" message={errorMessage} />);
    }
  }, [error, addToast]);

  return { user, isLoading };
};

export default useBiofluxUser;
